import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Globe, Volume2, Loader2, Check } from 'lucide-react';
import { 
  getTier1Languages, 
  getLanguageByCode,
  getStoredLanguage,
  LANGUAGE_PICKER_TEXT
} from '../lib/languages';
import api from '../lib/api';
import { toast } from 'sonner';
import { useTranslation } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * LanguagePickerDialog - A reusable dialog for selecting language with audio samples
 * 
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onOpenChange - Callback when dialog open state changes
 * @param {string} currentLanguage - The currently selected language code
 * @param {function} onLanguageChange - Callback when language is selected and confirmed
 */
export default function LanguagePickerDialog({
  open, 
  onOpenChange, 
  currentLanguage = 'en',
  onLanguageChange 
}) {
  const { t } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [audioRef, setAudioRef] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const languages = getTier1Languages();

  // Update selected language when currentLanguage prop changes or dialog opens
  useEffect(() => {
    if (open) {
      setSelectedLanguage(currentLanguage);
    }
  }, [currentLanguage, open]);

  // Cleanup audio when dialog closes
  useEffect(() => {
    if (!open && audioRef) {
      audioRef.pause();
      setAudioRef(null);
      setPlayingAudio(null);
    }
  }, [open, audioRef]);

  // Get UI text based on selected language or default to English
  const getText = (key) => {
    const lang = selectedLanguage || 'en';
    const langKey = lang === 'zh-CN' ? 'zh-CN' : lang;
    const texts = LANGUAGE_PICKER_TEXT[langKey] || LANGUAGE_PICKER_TEXT['en'];
    return texts[key] || LANGUAGE_PICKER_TEXT['en'][key];
  };

  const handleLanguageSelect = (langCode) => {
    setSelectedLanguage(langCode);
    // Stop any playing audio
    if (audioRef) {
      audioRef.pause();
      setAudioRef(null);
      setPlayingAudio(null);
    }
  };

  const handlePlaySample = async (langCode, e) => {
    e.stopPropagation();
    
    const lang = getLanguageByCode(langCode);
    if (!lang || !lang.samplePhrase) return;

    // Stop current audio if playing
    if (audioRef) {
      audioRef.pause();
      setAudioRef(null);
    }

    if (playingAudio === langCode) {
      setPlayingAudio(null);
      return;
    }

    setPlayingAudio(langCode);

    try {
      const response = await api.post('/ai/tts', {
        text: lang.samplePhrase,
        lang: langCode
      });

      if (response?.audio_url) {
        const audio = new Audio(`${API_URL}${response.audio_url}`);
        setAudioRef(audio);
        
        audio.onended = () => {
          setPlayingAudio(null);
          setAudioRef(null);
        };
        
        audio.onerror = (err) => {
          console.error('Audio error:', err);
          setPlayingAudio(null);
          setAudioRef(null);
          toast.error('Failed to play audio sample');
        };
        
        // Play with user interaction - required for mobile
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise.catch((err) => {
            console.error('Play error:', err);
            setPlayingAudio(null);
            setAudioRef(null);
            toast.error('Could not play audio - please try again');
          });
        }
      }
    } catch (error) {
      console.error('TTS error:', error);
      setPlayingAudio(null);
      toast.error('Failed to generate audio sample');
    }
  };

  const handleConfirm = async () => {
    if (!selectedLanguage) {
      toast.error('Please select a language');
      return;
    }

    setSaving(true);
    try {
      await onLanguageChange(selectedLanguage);
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving language:', error);
      toast.error('Failed to save language preference');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Globe className="h-6 w-6 text-primary" />
            {getText('title')}
          </DialogTitle>
          <DialogDescription>
            {getText('subtitle')}
          </DialogDescription>
        </DialogHeader>

        {/* Language Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 my-4">
          {languages.map((lang) => (
            <Card
              key={lang.code}
              className={`cursor-pointer transition-all hover:shadow-md ${
                selectedLanguage === lang.code
                  ? 'ring-2 ring-primary bg-primary/5'
                  : 'hover:bg-muted/50'
              }`}
              onClick={() => handleLanguageSelect(lang.code)}
              data-testid={`language-dialog-option-${lang.code}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-lg truncate" dir={lang.rtl ? 'rtl' : 'ltr'}>
                      {lang.nativeName}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {lang.name}
                    </p>
                  </div>
                  {selectedLanguage === lang.code && (
                    <Check className="h-5 w-5 text-primary flex-shrink-0 ml-2" />
                  )}
                </div>
                
                {/* Play Sample Button */}
                {lang.features?.voice && lang.samplePhrase && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full mt-2 text-xs"
                    onClick={(e) => handlePlaySample(lang.code, e)}
                    disabled={playingAudio && playingAudio !== lang.code}
                    aria-label={`Play ${lang.name} sample`}
                  >
                    {playingAudio === lang.code ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        {getText('playing')}
                      </>
                    ) : (
                      <>
                        <Volume2 className="h-3 w-3 mr-1" />
                        {getText('playSample')}
                      </>
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Selected language info */}
        {selectedLanguage && (
          <div className="bg-muted/30 border border-dashed rounded-lg p-4 text-center">
            <p className="text-sm text-muted-foreground">
              You selected <strong>{getLanguageByCode(selectedLanguage)?.name}</strong>.
              <br />
              The app will show everything in this language.
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm} 
            disabled={!selectedLanguage || saving}
            data-testid="language-dialog-confirm"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                {getText('continue')}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
